import { useCallback, useEffect, useRef } from 'react';
import { usePagesStateStore } from '@/stores';
import { useThrottle } from './useThrottle';

const SCROLL_SAVE_THROTTLE_MS = 150;

export function useScrollRestore(options: {
    pageKey: string | undefined;
    scrollContainer: HTMLDivElement | null;
    isReady: boolean;
}): void {
    const { pageKey, scrollContainer, isReady } = options;
    const { getPageState, setPageState } = usePagesStateStore();
    const scrollContainerRef = useRef(scrollContainer);
    const pageKeyRef = useRef(pageKey);

    scrollContainerRef.current = scrollContainer;
    pageKeyRef.current = pageKey;

    const saveScroll = useCallback(() => {
        const container = scrollContainerRef.current;
        const key = pageKeyRef.current;
        if (!container || !key) return;
        const current = getPageState(key);
        setPageState(key, {
            ...current,
            scrollPosition: container.scrollTop,
        });
    }, [getPageState, setPageState]);

    const throttledSave = useThrottle(saveScroll, SCROLL_SAVE_THROTTLE_MS);

    useEffect(() => {
        if (!scrollContainer || !pageKey) return;
        scrollContainer.addEventListener('scroll', throttledSave, {
            passive: true,
        });
        return () =>
            scrollContainer.removeEventListener('scroll', throttledSave);
    }, [scrollContainer, pageKey, throttledSave]);

    useEffect(() => {
        if (!pageKey || !scrollContainer || !isReady) return;
        const { scrollPosition } = getPageState(pageKey);
        if (scrollPosition <= 0) return;
        const frameId = requestAnimationFrame(() => {
            scrollContainer.scrollTop = scrollPosition;
        });
        return () => cancelAnimationFrame(frameId);
    }, [pageKey, scrollContainer, isReady, getPageState]);
}
